import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import toast from 'react-hot-toast';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Divider,
  Pagination,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import { Add, Edit, Delete, Visibility, CheckCircle, Cancel, FilterList } from '@mui/icons-material';
import { getCountries, getStates, getCities, normalizeCountryForForm, formatCountryForDisplay } from '../../data/locationData';
import { getTimezone } from '../../data/timezones';
import {
  getAvailableTimezones,
  formatTimezoneDisplay,
  normalizeTimezoneForFormSelect,
  normalizeTimezoneForStorage,
} from '../../utils/timezoneUtils';
import { formatCalendarDate, toCalendarYmd, calculateAgeFromCalendarYmd } from '../../utils/dateTimeUtils';
import { AGE_GROUPS, AGE_GROUP_LABELS, ageToAgeGroup, getAgeGroupSelectOptions } from '../../constants/ageGroups';
import studentsService from '../../services/students';
import { teachersAPI, allocationsAPI, notificationsAPI } from '../../services/api';
import StudentRegistrationForm from '../../components/StudentRegistrationForm';
import SalesPersonSelect from '../../components/SalesPersonSelect';
import StudentTimePreferencesDialog from '../../components/StudentTimePreferencesDialog';
import BatchAssignmentDialog from '../../components/BatchAssignmentDialog';
import DayScheduleSection from '../../components/DayScheduleSection';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { countryToCurrency, getCurrencyForCountry } from '../../data/countryCurrency';
import EnrollmentWorkflowPanel from '../../components/EnrollmentWorkflowPanel';
import StudentLeaveUnassignDialog from '../../components/StudentLeaveUnassignDialog';
import ParentConsentSummary from '../../components/ParentConsentSummary';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import { splitStoredName } from '../../utils/personName';

const Students = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin, isSales, isTeachingLead, canViewStudentFinancials, isParentFeedbackAgent, isBatchOps } = useAuth();
  const canEditStudent = isAdmin() || isTeachingLead() || isSales();
  const canManageEnrollment = isAdmin() || isSales();
  const showEnrollmentPanel = canManageEnrollment || isTeachingLead();
  const canSeeStudentFinancials = canViewStudentFinancials();
  const canUnassignBatch = canEditStudent && !isParentFeedbackAgent();
  const currencyOptions = [...new Set(Object.values(countryToCurrency))].sort();
  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    return {
      search: '',
      studentType: '',
      ageGroup: '',
      status: '',
      parentName: '',
      country: '',
      timezone: '',
      batchName: '',
      assignmentStatus: params.get('assignmentStatus') || '',
      teacherId: '',
      isActive: params.get('isActive') != null ? params.get('isActive') : 'true',
      created_from: params.get('created_from') || '',
      created_to: params.get('created_to') || '',
    };
  });
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [registrationFormOpen, setRegistrationFormOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [studentToEdit, setStudentToEdit] = useState(null);
  const [deletingStudentId, setDeletingStudentId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [timePreferencesDialogOpen, setTimePreferencesDialogOpen] = useState(false);
  const [studentForTimePreferences, setStudentForTimePreferences] = useState(null);
  const [batchAssignmentDialogOpen, setBatchAssignmentDialogOpen] = useState(false);
  const [studentForBatchAssignment, setStudentForBatchAssignment] = useState(null);
  const [editingTimePreferences, setEditingTimePreferences] = useState(false);
  const [dailyTimePreferences, setDailyTimePreferences] = useState({
    monday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
    tuesday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
    wednesday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
    thursday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
    friday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
    saturday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
    sunday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
  });
  const [timePreferencesTimezone, setTimePreferencesTimezone] = useState('UTC');
  const [unassigningAssignmentId, setUnassigningAssignmentId] = useState(null);
  const [leaveUnassign, setLeaveUnassign] = useState(null);
  const [siblingAnchorEl, setSiblingAnchorEl] = useState(null);
  const [siblingSourceStudent, setSiblingSourceStudent] = useState(null);
  const [siblingRows, setSiblingRows] = useState([]);
  const [siblingLoading, setSiblingLoading] = useState(false);

  // Student ID for which we show assignments (details or edit dialog)
  const studentIdForAssignments = (dialogOpen && selectedStudent?.id) ? selectedStudent.id : (editDialogOpen && studentToEdit?.id) ? studentToEdit.id : null;

  const { data: assignmentsResponse, refetch: refetchAssignments } = useQuery(
    ['student-assignments', studentIdForAssignments],
    () => allocationsAPI.getStudentAssignments(studentIdForAssignments),
    { enabled: !!studentIdForAssignments }
  );
  const studentAssignments = assignmentsResponse?.data?.assignments || [];

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const add = params.get('add');
    if (add === '1') {
      setRegistrationFormOpen(true);
    }
    const next = {};
    if (params.has('assignmentStatus')) next.assignmentStatus = params.get('assignmentStatus') || '';
    if (params.has('isActive')) next.isActive = params.get('isActive') ?? 'true';
    if (params.has('created_from')) next.created_from = params.get('created_from') || '';
    if (params.has('created_to')) next.created_to = params.get('created_to') || '';
    if (Object.keys(next).length > 0) {
      setFilters((prev) => ({ ...prev, ...next }));
      setPage(1);
    }
  }, [location.search]);

  // Fetch teachers for teacher filter dropdown
  const { data: teachersResponse } = useQuery(
    ['teachers', 'students-filter'],
    () => teachersAPI.getAll({ limit: 500 }),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 5 * 60 * 1000
    }
  );

  const teachers = teachersResponse?.data?.data || teachersResponse?.data || [];

  // Create a serialized filter key to ensure React Query detects changes
  const filterKey = JSON.stringify({
    search: filters.search || '',
    studentType: filters.studentType || '',
    ageGroup: filters.ageGroup || '',
    parentName: filters.parentName || '',
    country: filters.country || '',
    timezone: filters.timezone || '',
    batchName: filters.batchName || '',
    assignmentStatus: filters.assignmentStatus || '',
    teacherId: filters.teacherId || '',
    isActive: filters.isActive || '',
    created_from: filters.created_from || '',
    created_to: filters.created_to || '',
  });

  const { data: studentsResponse, isLoading, error } = useQuery(
    ['students', filterKey, page],
    () => {
      // Map frontend filter names to backend API parameter names
      const apiParams = {
        search: filters.search || undefined,
        student_type: filters.studentType || undefined,
        age_group: filters.ageGroup || undefined,
        status: 'registered', // Always filter for registered students
        parent_name: filters.parentName || undefined,
        country: filters.country || undefined,
        timezone: filters.timezone || undefined,
        batch_name: filters.batchName || undefined,
        assignment_status: filters.assignmentStatus || undefined,
        teacher_id: filters.teacherId || undefined,
        is_active: filters.isActive !== '' ? filters.isActive : undefined,
        created_from: filters.created_from || undefined,
        created_to: filters.created_to || undefined,
        page,
        limit
      };
      // Remove undefined values
      Object.keys(apiParams).forEach(key => apiParams[key] === undefined && delete apiParams[key]);
      return studentsService.getStudents(apiParams);
    },
    {
      keepPreviousData: true,
      staleTime: 0, // Always consider data stale to ensure fresh data on filter change
      refetchOnWindowFocus: false
    }
  );

  const students = studentsResponse?.data || [];
  const pagination = studentsResponse?.pagination || { page: 1, limit: 20, total: 0, pages: 1 };

  // Mutation for toggling active status with optimistic updates
  const toggleActiveMutation = useMutation(
    ({ id, isActive }) => studentsService.updateStudent(id, { is_active: isActive }),
    {
      onMutate: async ({ id, isActive }) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(['students']);

        // Get all query keys that match students queries
        const queryCache = queryClient.getQueryCache();
        const studentsQueries = queryCache.findAll(['students']);

        // Snapshot previous values for all matching queries
        const previousQueries = studentsQueries.map(query => ({
          queryKey: query.queryKey,
          data: query.state.data
        }));

        // Optimistically update all matching queries
        studentsQueries.forEach(query => {
          queryClient.setQueryData(query.queryKey, (old) => {
            if (!old?.data) return old;
            return {
              ...old,
              data: old.data.map((student) =>
                student.id === id ? { ...student, is_active: isActive } : student
              ),
            };
          });
        });

        // Return context with all previous values
        return { previousQueries };
      },
      onError: (err, variables, context) => {
        // Roll back all queries if mutation fails
        if (context?.previousQueries) {
          context.previousQueries.forEach(({ queryKey, data }) => {
            queryClient.setQueryData(queryKey, data);
          });
        }
        console.error('Error updating student status:', err);
        toast.error(err.response?.data?.message || 'Failed to update student status');
      },
      onSuccess: (response, variables) => {
        // Update with actual response data if available
        // Response structure: { success: true, data: { ...student data with is_active... } }
        const updatedStudent = response?.data?.data || response?.data;
        if (updatedStudent && updatedStudent.is_active !== undefined) {
          const queryCache = queryClient.getQueryCache();
          const studentsQueries = queryCache.findAll(['students']);
          studentsQueries.forEach(query => {
            queryClient.setQueryData(query.queryKey, (old) => {
              if (!old?.data) return old;
              return {
                ...old,
                data: old.data.map((student) =>
                  student.id === variables.id ? { ...student, is_active: updatedStudent.is_active } : student
                ),
              };
            });
          });
        }
        toast.success('Student status updated successfully');
      },
      onSettled: () => {
        // Always refetch after error or success to ensure we have the latest data
        queryClient.invalidateQueries(['students']);
      },
    }
  );

  const handleToggleActive = async (student) => {
    // Handle case where is_active might be undefined (default to true for backward compatibility)
    // Also handle string values like "true"/"false" from API
    const currentStatus = student.is_active !== undefined && student.is_active !== null
      ? (student.is_active === true || student.is_active === 'true')
      : true;
    const newStatus = !currentStatus;
    toggleActiveMutation.mutate({ id: student.id, isActive: newStatus });
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1); // Reset to first page when filters change
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewStudent = async (student) => {
    setDialogOpen(true);
    // Fetch full student details including time preferences
    try {
      const response = await studentsService.getStudent(student.id);
      setSelectedStudent(response.data);
      notificationsAPI.markStudentRead(student.id).catch(() => {});
    } catch (error) {
      console.error('Error fetching student details:', error);
      // Fallback to the student from list if fetch fails
      setSelectedStudent(student);
    }
  };

  const handleCloseSiblingPopover = () => {
    setSiblingAnchorEl(null);
    setSiblingSourceStudent(null);
    setSiblingRows([]);
  };

  const handleOpenSiblingPopover = async (event, student) => {
    event.stopPropagation();
    if (!student?.user_id) {
      toast.error('Cannot load siblings for this student');
      return;
    }
    setSiblingAnchorEl(event.currentTarget);
    setSiblingSourceStudent(student);
    setSiblingLoading(true);
    setSiblingRows([]);
    try {
      const response = await studentsService.getStudents({
        user_id: student.user_id,
        status: 'registered',
        is_active: '',
        page: 1,
        limit: 50,
      });
      setSiblingRows(response?.data || []);
    } catch (err) {
      console.error('Error loading sibling students:', err);
      toast.error('Failed to load linked students');
      setSiblingRows([]);
    } finally {
      setSiblingLoading(false);
    }
  };

  const handleShowAllSiblingsInList = (student) => {
    if (!student?.email && !student?.user_id) return;
    setFilters((prev) => ({
      ...prev,
      search: student.email || '',
      isActive: '',
      assignmentStatus: '',
      created_from: '',
      created_to: '',
    }));
    setPage(1);
    handleCloseSiblingPopover();
    toast.success('Showing all students for this parent email');
  };

  const handleViewSiblingFromPopover = (sibling) => {
    handleCloseSiblingPopover();
    handleViewStudent(sibling);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const studentId = params.get('studentId');
    if (!studentId) return;
    studentsService.getStudent(studentId).then((response) => {
      if (response?.data) {
        handleViewStudent(response.data);
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleEditStudent = async (student) => {
    // Fetch full student details including time preferences
    try {
      const response = await studentsService.getStudent(student.id);
      const fullStudent = response.data;
      setStudentToEdit(fullStudent);
      
      setEditFormData({
        // Personal Information
        first_name: fullStudent.first_name,
        last_name: fullStudent.last_name,
        phone: fullStudent.phone || '',
        date_of_birth: toCalendarYmd(fullStudent.date_of_birth),
        age: fullStudent.age || '',
        class_grade: fullStudent.class_grade || '',
        
        // Academic Information
        age_group: fullStudent.age_group,
        student_type: fullStudent.student_type,
        class_type_preference: fullStudent.class_type_preference,
        learning_level: fullStudent.learning_level,
        support_requirements: fullStudent.support_requirements || '',
        
        // School Information
        school_name: fullStudent.school_name || '',
        school_address: fullStudent.school_address || '',
        school_location: fullStudent.school_location || '',
        
        // Parent Information
        father_name: fullStudent.father_name || '',
        mother_name: fullStudent.mother_name || '',
        
        // Location Information
        country: normalizeCountryForForm(fullStudent.country),
        state: fullStudent.state || '',
        city: fullStudent.city || '',
        address: fullStudent.address || '',
        postal_code: fullStudent.postal_code || '',
        timezone: fullStudent.timezone || 'UTC',
        
        // Additional Information
        hobby: fullStudent.hobby || '',
        parent_fb_ids: fullStudent.parent_fb_ids || [],
        parent_linkedin_ids: fullStudent.parent_linkedin_ids || [],
        parent_instagram_ids: fullStudent.parent_instagram_ids || [],
        
        // Emergency Contact
        emergency_contact_name: fullStudent.emergency_contact_name || '',
        emergency_contact_phone: fullStudent.emergency_contact_phone || '',
        
        // Financial Information
        fees: fullStudent.fees || '',
        discount: fullStudent.discount || '',
        fee_currency: fullStudent.fee_currency || getCurrencyForCountry(fullStudent.country) || '',

        // Sales
        assigned_to: fullStudent.assigned_to || null,
        
        // Notes
        notes: fullStudent.notes || ''
      });
      
      // Initialize time preferences
      initializeTimePreferences(fullStudent);
      setEditingTimePreferences(true);
      setEditDialogOpen(true);
    } catch (error) {
      console.error('Error fetching student details:', error);
      // Fallback to the student from list if fetch fails
      setStudentToEdit(student);
      setEditFormData({
        first_name: student.first_name,
        last_name: student.last_name,
        phone: student.phone || '',
        country: normalizeCountryForForm(student.country),
        state: student.state || '',
        city: student.city || '',
        address: student.address || '',
        postal_code: student.postal_code || '',
        timezone: student.timezone || 'UTC',
      });
      initializeTimePreferences(student);
      setEditingTimePreferences(true);
      setEditDialogOpen(true);
    }
  };

  const handleOpenTimePreferences = (student) => {
    setStudentForTimePreferences(student);
    setTimePreferencesDialogOpen(true);
  };

  const handleCloseTimePreferences = () => {
    setTimePreferencesDialogOpen(false);
    setStudentForTimePreferences(null);
  };

  const handleAssignBatch = (student) => {
    setStudentForBatchAssignment(student);
    setBatchAssignmentDialogOpen(true);
  };

  const handleCloseBatchAssignment = () => {
    setBatchAssignmentDialogOpen(false);
    setStudentForBatchAssignment(null);
  };

  const handleBatchAssignmentSuccess = () => {
    queryClient.invalidateQueries(['students']);
    refetchAssignments();
    handleCloseBatchAssignment();
  };

  const handleUnassignFromStudent = async (assignmentId, payload) => {
    if (!assignmentId || !studentIdForAssignments) return;
    setUnassigningAssignmentId(assignmentId);
    try {
      const response = await allocationsAPI.unassignOnLeave(assignmentId, payload || {});
      toast.success(response?.data?.message || 'Student unassigned');
      queryClient.invalidateQueries(['students']);
      refetchAssignments();
    } catch (error) {
      console.error('Unassign error:', error);
      toast.error(error.response?.data?.message || 'Failed to unassign');
      throw error;
    } finally {
      setUnassigningAssignmentId(null);
    }
  };

  const handleSaveTimePreferences = async (timePreferences) => {
    try {
      await studentsService.updateTimePreferences(studentForTimePreferences.id, timePreferences);
      // Refresh the students list
      queryClient.invalidateQueries(['students', filters]);
      // Refresh the specific student data
      queryClient.invalidateQueries(['student', studentForTimePreferences.id]);
    } catch (error) {
      console.error('Error saving time preferences:', error);
      throw error; // Re-throw to let the dialog handle the error
    }
  };

  const getTimePreferencesStatus = (student) => {
    if (!student.time_preferences || student.time_preferences.length === 0) {
      return { status: 'not-set', label: 'Not Set', color: 'error' };
    }
    
    const totalSlots = student.time_preferences.length;
    const daysWithSlots = new Set(student.time_preferences.map(p => p.day_of_week)).size;
    
    if (daysWithSlots >= 5) {
      return { status: 'set', label: `Set (${totalSlots} slots)`, color: 'success' };
    } else if (daysWithSlots >= 1) {
      return { status: 'partial', label: `Partial (${totalSlots} slots)`, color: 'warning' };
    } else {
      return { status: 'not-set', label: 'Not Set', color: 'error' };
    }
  };

  // Initialize time preferences for editing (works with both selectedStudent and studentToEdit)
  const initializeTimePreferences = (student) => {
    if (!student) return;
    
    // Determine timezone first - convert IANA to abbreviation if needed
    let tz = 'UTC';
    if (student.time_preferences && student.time_preferences.length > 0 && student.time_preferences[0]?.timezone) {
      tz = student.time_preferences[0].timezone;
    } else if (student.timezone) {
      // Convert IANA timezone (e.g., "America/New_York") to abbreviation (e.g., "EST") for selector
      tz = normalizeTimezoneForFormSelect(student.timezone);
    } else if (student.country) {
      tz = getTimezone(student.country, student.state);
    }
    setTimePreferencesTimezone(tz);
    
    const dayMapping = { 0: 'sunday', 1: 'monday', 2: 'tuesday', 3: 'wednesday', 4: 'thursday', 5: 'friday', 6: 'saturday' };
    const newPreferences = {
      monday: { timezone: tz, slots: [], totalDayHours: 0 },
      tuesday: { timezone: tz, slots: [], totalDayHours: 0 },
      wednesday: { timezone: tz, slots: [], totalDayHours: 0 },
      thursday: { timezone: tz, slots: [], totalDayHours: 0 },
      friday: { timezone: tz, slots: [], totalDayHours: 0 },
      saturday: { timezone: tz, slots: [], totalDayHours: 0 },
      sunday: { timezone: tz, slots: [], totalDayHours: 0 },
    };

    // Convert student time preferences to teacher work hours format
    if (student.time_preferences && student.time_preferences.length > 0) {
      student.time_preferences.forEach((pref) => {
        const dayName = dayMapping[pref.day_of_week];
        if (!dayName) return;
        
        const startTime = pref.start_time ? pref.start_time.substring(0, 5) : '';
        const endTime = pref.end_time ? pref.end_time.substring(0, 5) : '';
        const start = new Date(`2000-01-01T${startTime}`);
        const end = new Date(`2000-01-01T${endTime}`);
        const totalHours = (end - start) / (1000 * 60 * 60);

        if (!newPreferences[dayName].slots) {
          newPreferences[dayName].slots = [];
        }
        newPreferences[dayName].slots.push({
          id: pref.id || `temp-${Date.now()}-${Math.random()}`,
          startTime,
          endTime,
          totalHours,
          isActive: pref.is_preferred !== false,
        });
      });

      // Calculate total hours for each day
      Object.keys(newPreferences).forEach(day => {
        newPreferences[day].totalDayHours = newPreferences[day].slots.reduce(
          (sum, slot) => sum + (slot.totalHours || 0),
          0
        );
      });
    }

    setDailyTimePreferences(newPreferences);
  };


  const handleAddTimeSlot = (dayName) => {
    setDailyTimePreferences(prev => {
      const newSlots = [
        ...(prev[dayName].slots || []),
        {
          id: `temp-${Date.now()}-${Math.random()}`,
          startTime: '',
          endTime: '',
          totalHours: 0,
          isActive: true,
        }
      ];
      const totalDayHours = newSlots.reduce((sum, slot) => sum + (slot.totalHours || 0), 0);
      return {
        ...prev,
        [dayName]: {
          ...prev[dayName],
          slots: newSlots,
          totalDayHours,
        }
      };
    });
  };

  const handleUpdateTimeSlot = (dayName, slotIndex, field, value) => {
    setDailyTimePreferences(prev => {
      const updatedSlots = prev[dayName].slots.map((slot, i) => {
        if (i === slotIndex) {
          const updated = { ...slot, [field]: value };
          // Recalculate totalHours if startTime or endTime changed
          if (field === 'startTime' || field === 'endTime') {
            if (updated.startTime && updated.endTime) {
              const start = new Date(`2000-01-01T${updated.startTime}`);
              const end = new Date(`2000-01-01T${updated.endTime}`);
              updated.totalHours = (end - start) / (1000 * 60 * 60);
            } else {
              updated.totalHours = 0;
            }
          }
          return updated;
        }
        return slot;
      });
      const totalDayHours = updatedSlots.reduce((sum, slot) => sum + (slot.totalHours || 0), 0);
      return {
        ...prev,
        [dayName]: {
          ...prev[dayName],
          slots: updatedSlots,
          totalDayHours,
        }
      };
    });
  };

  const handleDeleteTimeSlot = (dayName, slotIndex) => {
    setDailyTimePreferences(prev => {
      const updatedSlots = prev[dayName].slots.filter((_, i) => i !== slotIndex);
      const totalDayHours = updatedSlots.reduce((sum, slot) => sum + (slot.totalHours || 0), 0);
      return {
        ...prev,
        [dayName]: {
          ...prev[dayName],
          slots: updatedSlots,
          totalDayHours,
        }
      };
    });
  };


  const refreshEditEnrollmentStatus = async () => {
    if (!studentToEdit?.id) return;
    try {
      const response = await studentsService.getStudent(studentToEdit.id);
      if (response?.data) setStudentToEdit(response.data);
    } catch {
      // ignore refresh errors
    }
  };

  const saveEnrollmentFieldsBeforeNotify = async () => {
    if (!studentToEdit?.id) return false;
    try {
      await studentsService.updateStudent(studentToEdit.id, {
        fees: editFormData.fees === '' ? null : editFormData.fees,
        discount: editFormData.discount === '' ? null : editFormData.discount,
        fee_currency: editFormData.fee_currency === '' ? null : editFormData.fee_currency,
      });
      const response = await studentsService.getStudent(studentToEdit.id);
      if (response?.data) setStudentToEdit(response.data);
      queryClient.invalidateQueries('students');
      return true;
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not save fees before sending notification'));
      return false;
    }
  };

  const handleSaveEdit = async () => {
    try {
      setSavingEdit(true);
      console.log('🔍 Frontend - Starting student edit save:', {
        originalData: editFormData,
        fieldCount: Object.keys(editFormData).length
      });
      
      // Filter out email field and any undefined values; keep assigned_to when clearing (null)
      const updateData = Object.fromEntries(
        Object.entries(editFormData).filter(([key, value]) => {
          if (key === 'email' || value === undefined) return false;
          if (key === 'assigned_to') return true;
          return value !== null;
        })
      );
      if (updateData.assigned_to === '') {
        updateData.assigned_to = null;
      }
      if (updateData.date_of_birth === '') {
        updateData.date_of_birth = null;
      } else if (updateData.date_of_birth) {
        updateData.date_of_birth = toCalendarYmd(updateData.date_of_birth) || null;
      }
      if (updateData.timezone !== undefined) {
        updateData.timezone = normalizeTimezoneForStorage(updateData.timezone);
      }
      
      console.log('🔍 Frontend - Filtered update data:', {
        updateData: updateData,
        fieldCount: Object.keys(updateData).length,
        dataTypes: Object.fromEntries(
          Object.entries(updateData).map(([key, value]) => [key, typeof value])
        )
      });
      
      // Call the update API
      console.log('🔍 Frontend - Making API call to update student:', {
        studentId: studentToEdit.id,
        endpoint: `/api/students/${studentToEdit.id}`,
        method: 'PUT'
      });
      
      await studentsService.updateStudent(studentToEdit.id, updateData);
      
      console.log('✅ Frontend - Student update successful');
      
      // Save time preferences if they were edited
      if (editingTimePreferences) {
        // Validate time slots - only validate complete active slots (with both start and end times)
        const validationErrors = [];
        Object.entries(dailyTimePreferences).forEach(([day, dayData]) => {
          // Only consider slots that are active AND have both start and end times
          const completeActiveSlots = dayData.slots.filter(
            slot => slot.isActive && slot.startTime && slot.endTime
          );
          
          // Check for overlaps only among complete active slots
          if (completeActiveSlots.length > 1) {
            const sortedSlots = [...completeActiveSlots].sort((a, b) => a.startTime.localeCompare(b.startTime));
            
            for (let i = 0; i < sortedSlots.length - 1; i++) {
              const current = sortedSlots[i];
              const next = sortedSlots[i + 1];
              
              if (current.endTime > next.startTime) {
                validationErrors.push(`${day.charAt(0).toUpperCase() + day.slice(1)}: Time slots overlap (${current.startTime}-${current.endTime} and ${next.startTime}-${next.endTime})`);
              }
            }
          }
          
          // Validate time ranges for complete active slots
          completeActiveSlots.forEach((slot, index) => {
            const start = new Date(`2000-01-01T${slot.startTime}`);
            const end = new Date(`2000-01-01T${slot.endTime}`);
            if (start >= end) {
              validationErrors.push(`${day.charAt(0).toUpperCase() + day.slice(1)} Slot ${index + 1}: End time must be after start time`);
            }
          });
        });

        if (validationErrors.length === 0) {
          // Convert to API format
          const dayMapping = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 0 };
          const timePreferences = [];
          
          Object.entries(dailyTimePreferences).forEach(([dayName, dayData]) => {
            dayData.slots.forEach(slot => {
              if (slot.isActive && slot.startTime && slot.endTime) {
                timePreferences.push({
                  dayOfWeek: dayMapping[dayName],
                  startTime: slot.startTime,
                  endTime: slot.endTime,
                  isPreferred: slot.isActive,
                });
              }
            });
          });

          await studentsService.updateTimePreferences(studentToEdit.id, timePreferences);
          console.log('✅ Frontend - Time preferences update successful');
          
          // Note: Student timezone is already updated in editFormData when timezone selector changes,
          // so it will be saved automatically when handleSaveEdit runs
        } else {
          alert(`Please fix time preference errors:\n${validationErrors.join('\n')}`);
          return;
        }
      }
      
      // Close dialog and refresh data
      setEditDialogOpen(false);
      setStudentToEdit(null);
      setEditFormData({});
      setEditingTimePreferences(false);
      setDailyTimePreferences({
        monday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
        tuesday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
        wednesday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
        thursday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
        friday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
        saturday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
        sunday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
      });
      setTimePreferencesTimezone('UTC');
      queryClient.invalidateQueries('students');
      toast.success('Student updated successfully!');
    } catch (error) {
      console.error('❌ Frontend - Student update failed:', {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      toast.error(getApiErrorMessage(error, 'Failed to update student. Please try again.'));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setStudentToEdit(null);
    setEditFormData({});
    setSavingEdit(false);
    setEditingTimePreferences(false);
    setDailyTimePreferences({
      monday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
      tuesday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
      wednesday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
      thursday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
      friday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
      saturday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
      sunday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
    });
    setTimePreferencesTimezone('UTC');
  };

  const handleDeleteStudent = async (student) => {
    if (window.confirm(`Are you sure you want to delete ${student.first_name} ${student.last_name}?`)) {
      try {
        setDeletingStudentId(student.id);
        console.log('Deleting student:', student);
        // Call the delete API
        await studentsService.deleteStudent(student.id);
        // Refetch the students list
        queryClient.invalidateQueries('students');
        alert('Student deleted successfully!');
      } catch (error) {
        console.error('Error deleting student:', error);
        alert('Failed to delete student. Please try again.');
      } finally {
        setDeletingStudentId(null);
      }
    }
  };

  const handleRegistrationSuccess = () => {
    queryClient.invalidateQueries(['students']);
  };

  const handleCloseRegistration = () => {
    setRegistrationFormOpen(false);
    const params = new URLSearchParams(location.search);
    if (params.get('add') === '1') {
      params.delete('add');
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container>
        <Typography color="error" variant="h6">
          Error loading students: {error.message}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Students
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Search"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Search by name or email"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Student Type</InputLabel>
              <Select
                value={filters.studentType}
                onChange={(e) => handleFilterChange('studentType', e.target.value)}
                label="Student Type"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="autistic">Autistic</MenuItem>
                <MenuItem value="special_needs">Special Needs</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Age Group</InputLabel>
              <Select
                value={filters.ageGroup}
                onChange={(e) => handleFilterChange('ageGroup', e.target.value)}
                label="Age Group"
              >
                <MenuItem value="">All</MenuItem>
                {AGE_GROUPS.map((group) => (
                  <MenuItem key={group} value={group}>{AGE_GROUP_LABELS[group] || group}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Parent Name"
              value={filters.parentName}
              onChange={(e) => handleFilterChange('parentName', e.target.value)}
              placeholder="Search by father/mother name"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Country</InputLabel>
              <Select
                value={filters.country}
                onChange={(e) => handleFilterChange('country', e.target.value)}
                label="Country"
              >
                <MenuItem value="">All Countries</MenuItem>
                {getCountries().map((country) => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Timezone</InputLabel>
              <Select
                value={filters.timezone}
                onChange={(e) => handleFilterChange('timezone', e.target.value)}
                label="Timezone"
              >
                <MenuItem value="">All Timezones</MenuItem>
                {getAvailableTimezones().map((timezone) => (
                  <MenuItem key={timezone.value} value={timezone.value}>
                    {timezone.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Batch Name"
              value={filters.batchName}
              onChange={(e) => handleFilterChange('batchName', e.target.value)}
              placeholder="Type batch name to filter"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Assignment Status</InputLabel>
              <Select
                value={filters.assignmentStatus}
                onChange={(e) => handleFilterChange('assignmentStatus', e.target.value)}
                label="Assignment Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="unassigned">Unassigned</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Teacher</InputLabel>
              <Select
                value={filters.teacherId}
                onChange={(e) => handleFilterChange('teacherId', e.target.value)}
                label="Teacher"
              >
                <MenuItem value="">All Teachers</MenuItem>
                {teachers.map((teacher) => {
                  const teacherName = teacher.name || teacher.full_name || 
                    [teacher.first_name, teacher.last_name].filter(Boolean).join(' ').trim() || 
                    teacher.email || teacher.id;
                  return (
                    <MenuItem key={teacher.id} value={teacher.id}>
                      {teacherName}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.isActive}
                onChange={(e) => handleFilterChange('isActive', e.target.value)}
                label="Status"
              >
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
                <MenuItem value="">All</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {(filters.created_from || filters.created_to) && (
            <Grid item xs={12}>
              <Chip
                label={`Registered ${filters.created_from || '…'} → ${filters.created_to || '…'}`}
                onDelete={() => {
                  setFilters((prev) => ({ ...prev, created_from: '', created_to: '' }));
                  setPage(1);
                  const params = new URLSearchParams(location.search);
                  params.delete('created_from');
                  params.delete('created_to');
                  navigate({ pathname: location.pathname, search: params.toString() ? `?${params}` : '' }, { replace: true });
                }}
                sx={{ mr: 1 }}
              />
            </Grid>
          )}
          {canEditStudent && !isBatchOps() && (
          <Grid item xs={12} sm={3}>
            <Button
              variant="contained"
              startIcon={<Add />}
              fullWidth
              sx={{ height: '56px' }}
              onClick={() => setRegistrationFormOpen(true)}
            >
              Add Student
            </Button>
          </Grid>
          )}
        </Grid>

        {/* Pagination Info at Top */}
        {pagination.total > 0 && (
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} students
            </Typography>
            <Pagination
              count={pagination.pages}
              page={pagination.page}
              onChange={handlePageChange}
              color="primary"
              size="medium"
              showFirstButton
              showLastButton
            />
          </Box>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Age Group</TableCell>
                <TableCell>Student Type</TableCell>
                <TableCell>Class Preference</TableCell>
                <TableCell>Learning Level</TableCell>
                <TableCell>Time Preferences</TableCell>
                <TableCell>Batch</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students?.map((student) => (
                <TableRow key={student.id}>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {student.display_id || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <span>{student.first_name} {student.last_name}</span>
                      {student.total_students_for_user > 1 && (
                        <Tooltip title="View linked students (siblings)">
                          <Chip
                            label={`${student.total_students_for_user} students`}
                            size="small"
                            color="info"
                            variant="outlined"
                            onClick={(e) => handleOpenSiblingPopover(e, student)}
                            sx={{ fontSize: '0.7rem', height: '20px', cursor: 'pointer' }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <span>{student.email}</span>
                      {student.total_students_for_user > 1 && (
                        <Tooltip title="View linked students (siblings)">
                          <Chip
                            label={`${student.total_students_for_user}x`}
                            size="small"
                            color="secondary"
                            onClick={(e) => handleOpenSiblingPopover(e, student)}
                            sx={{ fontSize: '0.65rem', height: '18px', minWidth: '24px', cursor: 'pointer' }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={student.age_group} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={student.student_type} 
                      size="small"
                      color={student.student_type === 'normal' ? 'default' : 'secondary'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={student.class_type_preference} 
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={student.learning_level} 
                      size="small"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const status = getTimePreferencesStatus(student);
                      return (
                        <Chip 
                          label={status.label} 
                          size="small"
                          color={status.color}
                          onClick={canEditStudent ? () => handleOpenTimePreferences(student) : undefined}
                          style={{ cursor: canEditStudent ? 'pointer' : 'default' }}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={
                        student.active_assignments > 0
                          ? (student.batch_names || student.batch_name || `${student.active_assignments} batch(es)`)
                          : 'Unassigned'
                      } 
                      size="small"
                      color={student.active_assignments > 0 ? 'success' : 'warning'}
                      title={student.batch_names || student.batch_name || ''}
                    />
                  </TableCell>
                  <TableCell>
                    {(() => {
                      // Ensure boolean conversion - handle both boolean and string values
                      const isActive = student.is_active !== undefined && student.is_active !== null
                        ? (student.is_active === true || student.is_active === 'true')
                        : true; // Default to true if undefined
                      return (
                        <Chip 
                          icon={isActive ? <CheckCircle /> : <Cancel />}
                          label={isActive ? 'Active' : 'Inactive'} 
                          size="small"
                          color={isActive ? 'success' : 'default'}
                          variant={isActive ? 'filled' : 'outlined'}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Student Details">
                      <IconButton onClick={() => handleViewStudent(student)}>
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Student">
                      <span>
                        <IconButton onClick={() => handleEditStudent(student)} disabled={!canEditStudent}>
                          <Edit />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {canEditStudent && (() => {
                      // Ensure boolean conversion - handle both boolean and string values
                      const isActive = student.is_active !== undefined && student.is_active !== null
                        ? (student.is_active === true || student.is_active === 'true')
                        : true; // Default to true if undefined
                      return (isAdmin() || isTeachingLead()) ? (
                        <Tooltip title={isActive ? 'Deactivate Student' : 'Activate Student'}>
                          <IconButton 
                            onClick={() => handleToggleActive(student)}
                            disabled={toggleActiveMutation.isLoading}
                            color={isActive ? 'error' : 'success'}
                          >
                            {isActive ? <Cancel /> : <CheckCircle />}
                          </IconButton>
                        </Tooltip>
                      ) : null;
                    })()}
                    <Tooltip title="Assign to another batch">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleAssignBatch(student)}
                        sx={{ mr: 1 }}
                        disabled={!canEditStudent}
                      >
                        {student.active_assignments > 0 ? 'Add Batch' : 'Assign Batch'}
                      </Button>
                    </Tooltip>
                    {isAdmin() && (
                    <Tooltip title="Delete Student">
                      <span>
                        <IconButton onClick={() => handleDeleteStudent(student)} disabled={deletingStudentId === student.id}>
                          {deletingStudentId === student.id ? <CircularProgress size={20} /> : <Delete />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {pagination.total > 0 && (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 3, mb: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2" color="text.secondary">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} students
              </Typography>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          </Box>
        )}
      </Box>

      <Popover
        open={Boolean(siblingAnchorEl)}
        anchorEl={siblingAnchorEl}
        onClose={handleCloseSiblingPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { minWidth: 320, maxWidth: 420 } }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Linked students
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {siblingSourceStudent?.email || 'Same parent account'}
          </Typography>
        </Box>
        <Divider />
        {siblingLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <List dense disablePadding>
            {siblingRows.length === 0 ? (
              <ListItemButton disabled>
                <ListItemText primary="No linked students found" />
              </ListItemButton>
            ) : (
              siblingRows.map((sibling) => {
                const isCurrent = siblingSourceStudent && String(sibling.id) === String(siblingSourceStudent.id);
                const active =
                  sibling.is_active === true || sibling.is_active === 'true' || sibling.is_active == null;
                return (
                  <ListItemButton
                    key={sibling.id}
                    selected={isCurrent}
                    onClick={() => handleViewSiblingFromPopover(sibling)}
                  >
                    <ListItemText
                      primary={`${sibling.first_name || ''} ${sibling.last_name || ''}`.trim() || 'Student'}
                      secondary={`${sibling.display_id || sibling.id}${active ? '' : ' · Inactive'}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewSiblingFromPopover(sibling);
                        }}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItemButton>
                );
              })
            )}
          </List>
        )}
        <Divider />
        <Box sx={{ p: 1.5 }}>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            startIcon={<FilterList />}
            disabled={!siblingSourceStudent}
            onClick={() => handleShowAllSiblingsInList(siblingSourceStudent)}
          >
            Show all in list
          </Button>
        </Box>
      </Popover>

      {/* Student Details Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Student Details - {selectedStudent?.first_name} {selectedStudent?.last_name}</Typography>
        </DialogTitle>
        <DialogContent>
          {selectedStudent && (
            <Box sx={{ mt: 2 }}>
              {/* Personal Information */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                Personal Information
              </Typography>
              <Grid container spacing={2}>
                {selectedStudent.display_id && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Student ID</Typography>
                    <Typography variant="body1">{selectedStudent.display_id}</Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Full Name</Typography>
                  <Typography variant="body1">
                    {selectedStudent.first_name} {selectedStudent.last_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedStudent.email}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedStudent.phone || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Date of Birth</Typography>
                  <Typography variant="body1">
                    {selectedStudent.date_of_birth ? formatCalendarDate(selectedStudent.date_of_birth) : 'Not provided'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Age</Typography>
                  <Typography variant="body1">{selectedStudent.age || 'Not calculated'} years old</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Class Grade</Typography>
                  <Typography variant="body1">{selectedStudent.class_grade || 'Not provided'}</Typography>
                </Grid>
              </Grid>

              {/* Academic Information */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
                Academic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Age Group</Typography>
                  <Typography variant="body1">{selectedStudent.age_group}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Student Type</Typography>
                  <Typography variant="body1">{selectedStudent.student_type}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Class Preference</Typography>
                  <Typography variant="body1">{selectedStudent.class_type_preference}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Learning Level</Typography>
                  <Typography variant="body1">{selectedStudent.learning_level}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Support Requirements</Typography>
                  <Typography variant="body1">{selectedStudent.support_requirements || 'None specified'}</Typography>
                </Grid>
              </Grid>

              {/* School Information */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
                School Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">School Name</Typography>
                  <Typography variant="body1">{selectedStudent.school_name || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">School Location</Typography>
                  <Typography variant="body1">{selectedStudent.school_location || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">School Address</Typography>
                  <Typography variant="body1">{selectedStudent.school_address || 'Not provided'}</Typography>
                </Grid>
              </Grid>

              {/* Parent Information */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
                Parent Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Father&apos;s First Name</Typography>
                  <Typography variant="body1">{splitStoredName(selectedStudent.father_name).first || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Father&apos;s Last Name</Typography>
                  <Typography variant="body1">{splitStoredName(selectedStudent.father_name).last || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Mother&apos;s First Name</Typography>
                  <Typography variant="body1">{splitStoredName(selectedStudent.mother_name).first || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Mother&apos;s Last Name</Typography>
                  <Typography variant="body1">{splitStoredName(selectedStudent.mother_name).last || 'Not provided'}</Typography>
                </Grid>
              </Grid>

              <ParentConsentSummary consent={selectedStudent.parent_consent} />

              {/* Location Information */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
                Location Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Country</Typography>
                  <Typography variant="body1">{formatCountryForDisplay(selectedStudent.country) || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">State</Typography>
                  <Typography variant="body1">{selectedStudent.state || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">City</Typography>
                  <Typography variant="body1">{selectedStudent.city || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Zip code</Typography>
                  <Typography variant="body1">{selectedStudent.postal_code || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Timezone</Typography>
                  <Typography variant="body1">{formatTimezoneDisplay(selectedStudent.timezone || 'UTC')}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                  <Typography variant="body1">{selectedStudent.address || 'Not provided'}</Typography>
                </Grid>
              </Grid>

              {/* Additional Information */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
                Additional Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Sales Person</Typography>
                  <Typography variant="body1">
                    {selectedStudent.sales_person_name || 'Unassigned'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">RM</Typography>
                  <Typography variant="body1">
                    {selectedStudent.rm_name || 'Unassigned'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">First class</Typography>
                  <Typography variant="body1">
                    {selectedStudent.first_class_date
                      ? formatCalendarDate(selectedStudent.first_class_date)
                      : 'Not yet'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Hobby</Typography>
                  <Typography variant="body1">{selectedStudent.hobby || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip 
                    label={selectedStudent.status} 
                    color={selectedStudent.status === 'registered' ? 'success' : 'default'}
                    size="small"
                  />
                </Grid>
              </Grid>

              {/* Social Media Information */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
                Social Media Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">Facebook IDs</Typography>
                  <Box sx={{ mt: 1 }}>
                    {selectedStudent.parent_fb_ids && selectedStudent.parent_fb_ids.length > 0 ? (
                      selectedStudent.parent_fb_ids.map((id, index) => (
                        <Chip key={index} label={id} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">None provided</Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">LinkedIn IDs</Typography>
                  <Box sx={{ mt: 1 }}>
                    {selectedStudent.parent_linkedin_ids && selectedStudent.parent_linkedin_ids.length > 0 ? (
                      selectedStudent.parent_linkedin_ids.map((id, index) => (
                        <Chip key={index} label={id} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">None provided</Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">Instagram IDs</Typography>
                  <Box sx={{ mt: 1 }}>
                    {selectedStudent.parent_instagram_ids && selectedStudent.parent_instagram_ids.length > 0 ? (
                      selectedStudent.parent_instagram_ids.map((id, index) => (
                        <Chip key={index} label={id} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">None provided</Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {/* Emergency Contact */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
                Emergency Contact
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Contact Name</Typography>
                  <Typography variant="body1">{selectedStudent.emergency_contact_name || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Contact Phone</Typography>
                  <Typography variant="body1">{selectedStudent.emergency_contact_phone || 'Not provided'}</Typography>
                </Grid>
              </Grid>

              {/* Financial Information */}
              {canSeeStudentFinancials && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
                    Financial Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Fees</Typography>
                      <Typography variant="body1">
                        {selectedStudent.fees != null && selectedStudent.fees !== ''
                          ? `${selectedStudent.fees}${selectedStudent.fee_currency ? ` ${selectedStudent.fee_currency}` : ''}`
                          : 'Not specified'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Currency</Typography>
                      <Typography variant="body1">{selectedStudent.fee_currency || 'Not specified'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Discount</Typography>
                      <Typography variant="body1">
                        {selectedStudent.discount != null && selectedStudent.discount !== ''
                          ? `${selectedStudent.discount}%`
                          : 'None'}
                      </Typography>
                    </Grid>
                  </Grid>
                </>
              )}

              {showEnrollmentPanel && selectedStudent?.id && (
                <EnrollmentWorkflowPanel
                  studentId={selectedStudent.id}
                  enrollmentStatus={selectedStudent.enrollment_status}
                  fees={canSeeStudentFinancials ? selectedStudent.fees : undefined}
                  feeCurrency={canSeeStudentFinancials ? selectedStudent.fee_currency : undefined}
                  canManage={false}
                  onStatusChange={async () => {
                    const response = await studentsService.getStudent(selectedStudent.id);
                    if (response?.data) setSelectedStudent(response.data);
                  }}
                />
              )}

              {/* Batch Assignment */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
                Batch Assignment
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Current Batches</Typography>
                  {studentAssignments.length === 0 ? (
                    <Typography variant="body1" color="text.secondary">Unassigned</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
                      {studentAssignments.map((a) => (
                        <Box key={a.assignment_id} sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body1">{a.batch_name || a.batch_id}</Typography>
                          <Typography variant="body1">
                            Teacher: {a.teacher_name || 'Not assigned'}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Age Group</Typography>
                  <Typography variant="body1">
                    {selectedStudent.age_group || 'Not set'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Time Preferences</Typography>
                  <Typography variant="body1">
                    {selectedStudent.time_preferences?.length || 0} slots configured
                  </Typography>
                </Grid>
              </Grid>

              {/* Notes */}
              {selectedStudent.notes && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
                    Notes
                  </Typography>
                  <Typography variant="body1" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    {selectedStudent.notes}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Student Registration Form */}
      <StudentRegistrationForm
        open={registrationFormOpen}
        onClose={handleCloseRegistration}
        onSuccess={handleRegistrationSuccess}
      />

      {/* Edit Student Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Edit Student - {studentToEdit?.first_name} {studentToEdit?.last_name}</Typography>
        </DialogTitle>
        <DialogContent>
          {studentToEdit && (
            <Box sx={{ mt: 2 }}>
              {/* Personal Information */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                Personal Information
              </Typography>
              <Grid container spacing={2}>
                {studentToEdit.display_id && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Student ID"
                      value={studentToEdit.display_id}
                      variant="outlined"
                      size="small"
                      disabled
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={editFormData.first_name || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, first_name: e.target.value }))}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={editFormData.last_name || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, last_name: e.target.value }))}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={studentToEdit.email || ''}
                    variant="outlined"
                    size="small"
                    type="email"
                    disabled
                    helperText="Email cannot be changed"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={editFormData.phone || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, phone: e.target.value }))}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                                  <TextField
                  fullWidth
                  label="Date of Birth"
                  value={toCalendarYmd(editFormData.date_of_birth)}
                  onChange={(e) => {
                    const dob = e.target.value;
                    const age = dob ? calculateAgeFromCalendarYmd(dob) : null;
                    const group = ageToAgeGroup(age);
                    setEditFormData(prev => ({
                      ...prev,
                      date_of_birth: dob,
                      age: age != null ? String(age) : prev.age,
                      age_group: group || prev.age_group,
                    }));
                  }}
                  variant="outlined"
                  size="small"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Age"
                    value={editFormData.age || ''}
                    onChange={(e) => {
                      const age = e.target.value;
                      const group = ageToAgeGroup(age);
                      setEditFormData(prev => ({
                        ...prev,
                        age,
                        age_group: group || prev.age_group,
                      }));
                    }}
                    variant="outlined"
                    size="small"
                    type="number"
                    helperText="Auto-calculated from date of birth"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Class Grade"
                    value={editFormData.class_grade || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, class_grade: e.target.value }))}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Academic Information */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                Academic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Age Group</InputLabel>
                    <Select
                      value={editFormData.age_group || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, age_group: e.target.value }))}
                      label="Age Group"
                    >
                      {getAgeGroupSelectOptions(editFormData.age_group).map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Student Type</InputLabel>
                    <Select
                      value={editFormData.student_type || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, student_type: e.target.value }))}
                      label="Student Type"
                    >
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="autistic">Autistic</MenuItem>
                      <MenuItem value="special_needs">Special Needs</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Class Preference</InputLabel>
                    <Select
                      value={editFormData.class_type_preference || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, class_type_preference: e.target.value }))}
                      label="Class Preference"
                    >
                      <MenuItem value="group">Group</MenuItem>
                      <MenuItem value="one_on_one">One-on-One</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Learning Level</InputLabel>
                    <Select
                      value={editFormData.learning_level || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, learning_level: e.target.value }))}
                      label="Learning Level"
                    >
                      <MenuItem value="beginner">Beginner</MenuItem>
                      <MenuItem value="intermediate">Intermediate</MenuItem>
                      <MenuItem value="advanced">Advanced</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Support Requirements"
                    value={editFormData.support_requirements || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, support_requirements: e.target.value }))}
                    variant="outlined"
                    size="small"
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* School Information */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                School Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="School Name"
                    value={editFormData.school_name || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, school_name: e.target.value }))}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="School Location"
                    value={editFormData.school_location || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, school_location: e.target.value }))}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="School Address"
                    value={editFormData.school_address || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, school_address: e.target.value }))}
                    variant="outlined"
                    size="small"
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Parent Information */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                Parent Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Father's Name"
                    value={editFormData.father_name || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, father_name: e.target.value }))}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Mother's Name"
                    value={editFormData.mother_name || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, mother_name: e.target.value }))}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Batch Assignment (edit) */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                Batch Assignment
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Current Batches</Typography>
                  {studentAssignments.length === 0 ? (
                    <Typography variant="body1" color="text.secondary">Unassigned</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
                      {studentAssignments.map((a) => (
                        <Box key={a.assignment_id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Box>
                            <Typography variant="body1">{a.batch_name || a.batch_id}</Typography>
                            <Typography variant="body1">
                              Teacher: {a.teacher_name || 'Not assigned'}
                            </Typography>
                          </Box>
                          {canUnassignBatch ? (
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              onClick={() => setLeaveUnassign({ assignmentId: a.assignment_id, batchName: a.batch_name })}
                              disabled={unassigningAssignmentId === a.assignment_id}
                            >
                              {unassigningAssignmentId === a.assignment_id ? <CircularProgress size={16} /> : 'Unassign'}
                            </Button>
                          ) : null}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Location Information */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                Location Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Country</InputLabel>
                    <Select
                      value={editFormData.country || ''}
                      onChange={(e) => {
                        const newCountry = e.target.value;
                        const currentState = editFormData.state;
                        
                        // Check if current state/city are valid for new country
                        if (currentState && !getStates(newCountry).includes(currentState)) {
                          alert(`State "${currentState}" is not valid for ${newCountry}. State and city will be cleared.`);
                        }
                        
                        setEditFormData(prev => ({ 
                          ...prev, 
                          country: newCountry,
                          state: '', // Clear state when country changes
                          city: '',   // Clear city when country changes
                          fee_currency: prev.fee_currency || getCurrencyForCountry(newCountry),
                        }));
                      }}
                      label="Country"
                    >
                      {getCountries().map((country) => (
                        <MenuItem key={country} value={country}>
                          {country}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>State</InputLabel>
                    <Select
                      value={editFormData.state || ''}
                      onChange={(e) => {
                        const newState = e.target.value;
                        const currentCity = editFormData.city;
                        
                        // Check if current city is valid for new state
                        if (currentCity && !getCities(editFormData.country, newState).includes(currentCity)) {
                          alert(`City "${currentCity}" is not valid for ${newState}. City will be cleared.`);
                        }
                        
                        setEditFormData(prev => ({ 
                          ...prev, 
                          state: newState,
                          city: '' // Clear city when state changes
                        }));
                      }}
                      label="State"
                      disabled={!editFormData.country}
                    >
                      {editFormData.country && getStates(editFormData.country).map((state) => (
                        <MenuItem key={state} value={state}>
                          {state}
                        </MenuItem>
                      ))}
                      {editFormData.state &&
                        editFormData.country &&
                        !getStates(editFormData.country).includes(editFormData.state) && (
                          <MenuItem value={editFormData.state}>{editFormData.state} (saved)</MenuItem>
                        )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>City</InputLabel>
                    <Select
                      value={editFormData.city || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, city: e.target.value }))}
                      label="City"
                      disabled={!editFormData.state}
                    >
                      {editFormData.state && getCities(editFormData.country, editFormData.state).map((city) => (
                        <MenuItem key={city} value={city}>
                          {city}
                        </MenuItem>
                      ))}
                      {editFormData.city &&
                        editFormData.country &&
                        editFormData.state &&
                        !getCities(editFormData.country, editFormData.state).includes(editFormData.city) && (
                          <MenuItem value={editFormData.city}>{editFormData.city} (saved)</MenuItem>
                        )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Zip code"
                    value={editFormData.postal_code || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, postal_code: e.target.value }))}
                    variant="outlined"
                    size="small"
                    inputProps={{ maxLength: 20 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Timezone</InputLabel>
                    <Select
                      value={normalizeTimezoneForFormSelect(editFormData.timezone)}
                      onChange={(e) => {
                        const newTimezoneAbbrev = e.target.value;
                        const newTimezoneStored = normalizeTimezoneForStorage(newTimezoneAbbrev);
                        setEditFormData(prev => ({ ...prev, timezone: newTimezoneStored }));
                        setTimePreferencesTimezone(newTimezoneAbbrev);
                        setDailyTimePreferences(prev => {
                          const updated = {};
                          Object.keys(prev).forEach(day => {
                            updated[day] = {
                              ...prev[day],
                              timezone: newTimezoneAbbrev
                            };
                          });
                          return updated;
                        });
                      }}
                      label="Timezone"
                    >
                      {getAvailableTimezones().map((tz) => (
                        <MenuItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </MenuItem>
                      ))}
                      {(() => {
                        const tzSelect = normalizeTimezoneForFormSelect(editFormData.timezone);
                        const known = getAvailableTimezones().map((t) => t.value);
                        if (!tzSelect || known.includes(tzSelect)) return null;
                        return (
                          <MenuItem key={`legacy-${tzSelect}`} value={tzSelect}>
                            {formatTimezoneDisplay(tzSelect)}
                          </MenuItem>
                        );
                      })()}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Street address"
                    value={editFormData.address || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, address: e.target.value }))}
                    variant="outlined"
                    size="small"
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Additional Information */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                Additional Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Hobby"
                    value={editFormData.hobby || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, hobby: e.target.value }))}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                {canSeeStudentFinancials ? (
                  <>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Fees"
                        value={editFormData.fees || ''}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, fees: e.target.value }))}
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Currency</InputLabel>
                        <Select
                          value={editFormData.fee_currency || ''}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, fee_currency: e.target.value }))}
                          label="Currency"
                        >
                          {currencyOptions.map((c) => (
                            <MenuItem key={c} value={c}>{c}</MenuItem>
                          ))}
                          {editFormData.fee_currency && !currencyOptions.includes(editFormData.fee_currency) && (
                            <MenuItem value={editFormData.fee_currency}>{editFormData.fee_currency}</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Discount"
                        value={editFormData.discount || ''}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, discount: e.target.value }))}
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
                  </>
                ) : null}
              </Grid>

              {canManageEnrollment && studentToEdit?.id && (
                <EnrollmentWorkflowPanel
                  studentId={studentToEdit.id}
                  enrollmentStatus={studentToEdit.enrollment_status}
                  fees={canSeeStudentFinancials ? editFormData.fees : undefined}
                  feeCurrency={canSeeStudentFinancials ? editFormData.fee_currency : undefined}
                  canManage={canManageEnrollment}
                  onBeforeNotify={saveEnrollmentFieldsBeforeNotify}
                  onStatusChange={refreshEditEnrollmentStatus}
                />
              )}

              <Divider sx={{ my: 3 }} />

              {/* Emergency Contact */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                Emergency Contact
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Emergency Contact Name"
                    value={editFormData.emergency_contact_name || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, emergency_contact_name: e.target.value }))}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Emergency Contact Phone"
                    value={editFormData.emergency_contact_phone || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, emergency_contact_phone: e.target.value }))}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Time Preferences */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                Time Preferences
              </Typography>
              <Box sx={{ mb: 3 }}>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Timezone *</InputLabel>
                  <Select
                    value={timePreferencesTimezone}
                    onChange={(e) => {
                      const newTimezoneAbbrev = e.target.value;
                      setTimePreferencesTimezone(newTimezoneAbbrev);
                      setEditFormData(prev => ({
                        ...prev,
                        timezone: normalizeTimezoneForStorage(newTimezoneAbbrev),
                      }));
                      setDailyTimePreferences(prev => {
                        const updated = {};
                        Object.keys(prev).forEach(day => {
                          updated[day] = {
                            ...prev[day],
                            timezone: newTimezoneAbbrev
                          };
                        });
                        return updated;
                      });
                    }}
                    label="Timezone *"
                  >
                    {getAvailableTimezones().map((tz) => (
                      <MenuItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </MenuItem>
                    ))}
                    {timePreferencesTimezone &&
                      !getAvailableTimezones().some((t) => t.value === timePreferencesTimezone) && (
                        <MenuItem value={timePreferencesTimezone}>
                          {formatTimezoneDisplay(timePreferencesTimezone)}
                        </MenuItem>
                      )}
                  </Select>
                </FormControl>
                {Object.entries(dailyTimePreferences).map(([dayName, dayData]) => (
                  <Box key={dayName} sx={{ mb: 2 }}>
                    <DayScheduleSection
                      dayName={dayName}
                      dayData={dayData}
                      onUpdate={handleUpdateTimeSlot}
                      onAddSlot={handleAddTimeSlot}
                      onDeleteSlot={handleDeleteTimeSlot}
                    />
                  </Box>
                ))}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Sales & Notes */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                Sales & Notes
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <SalesPersonSelect
                    value={editFormData.assigned_to || ''}
                    onChange={(v) => setEditFormData((prev) => ({ ...prev, assigned_to: v }))}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Notes */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                Notes
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    value={editFormData.notes || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, notes: e.target.value }))}
                    variant="outlined"
                    size="small"
                    multiline
                    rows={4}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSaveEdit}
            disabled={savingEdit}
          >
            {savingEdit ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Student Time Preferences Dialog */}
      {studentForTimePreferences && (
        <StudentTimePreferencesDialog
          open={timePreferencesDialogOpen}
          onClose={handleCloseTimePreferences}
          student={studentForTimePreferences}
          onSave={handleSaveTimePreferences}
        />
      )}

      {/* Batch Assignment Dialog */}
      {studentForBatchAssignment && (
        <BatchAssignmentDialog
          open={batchAssignmentDialogOpen}
          onClose={handleCloseBatchAssignment}
          student={studentForBatchAssignment}
          onSuccess={handleBatchAssignmentSuccess}
        />
      )}

      <StudentLeaveUnassignDialog
        open={Boolean(leaveUnassign)}
        studentName={[studentToEdit?.first_name, studentToEdit?.last_name].filter(Boolean).join(' ')}
        batchName={leaveUnassign?.batchName || ''}
        submitting={Boolean(leaveUnassign && unassigningAssignmentId === leaveUnassign.assignmentId)}
        onClose={() => setLeaveUnassign(null)}
        onConfirm={async (payload) => {
          await handleUnassignFromStudent(leaveUnassign.assignmentId, payload);
          setLeaveUnassign(null);
        }}
      />
    </Container>
  );
};

export default Students; 